// CleanModernLayout.js - Shared layout with expandable left navigation
// Used across Command Center, Assets, Asset Details, and other pages

import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getAssets } from '../../services/assetService';
import { useNavState } from '../../contexts/NavContext';
import './CleanModernLayout.css';

function CleanModernLayout({ children, activePage }) {
    const location = useLocation();

    // Nav collapsed state — shared with Navbar's hamburger via NavContext.
    const { navCollapsed } = useNavState();

    // Expandable sections state (persisted in localStorage + auto-expand active section)
    const [sectionsExpanded, setSectionsExpanded] = useState(() => {
        const path = window.location.pathname;
        const saved = localStorage.getItem('navSectionsExpanded');
        const base = saved ? JSON.parse(saved) : { assets: true, drifts: false, scans: false };
        // Auto-expand the section matching the current route
        if (path.startsWith('/assets')) base.assets = true;
        if (path.startsWith('/drift-')) base.drifts = true;
        if (path.startsWith('/fast-scan') || path.startsWith('/comprehensive-scan') || path.startsWith('/schedules')) base.scans = true;
        return base;
    });

    // Assets data for the sidebar
    const [assets, setAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(true);

    // Persist nav sections expanded state
    useEffect(() => {
        localStorage.setItem('navSectionsExpanded', JSON.stringify(sectionsExpanded));
    }, [sectionsExpanded]);

    // Load assets for sidebar navigation
    useEffect(() => {
        const loadAssets = async () => {
            try {
                const data = await getAssets();
                setAssets(data.assets || []);
            } catch (err) {
                console.error('Failed to load assets for nav:', err);
            } finally {
                setAssetsLoading(false);
            }
        };
        loadAssets();
    }, []);

    // Group assets by type
    const domains = assets.filter(a => a.assetType === 'domain');
    const ipAssets = assets.filter(a => a.assetType === 'ip' || a.assetType === 'cidr');

    // Toggle section expansion
    const toggleSection = (section) => {
        setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Check if a path is active
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    // Get risk dot class
    const getRiskDotClass = (asset) => {
        if (!asset.riskScore && asset.riskScore !== 0) return 'healthy';
        if (asset.riskScore < 50) return 'critical';
        if (asset.riskScore < 70) return 'at-risk';
        if (asset.riskScore < 85) return 'warning';
        return 'healthy';
    };

    return (
        <div className={`cm-layout ${navCollapsed ? 'nav-collapsed' : ''}`}>
            <nav className={`cm-left-nav ${navCollapsed ? 'collapsed' : ''}`}>
                {/* Navigation Items */}
                <div className="nav-items">
                    {/* Overview - Single Link */}
                    <Link
                        to="/command-center"
                        className={`nav-item ${isActive('/command-center') ? 'active' : ''}`}
                        title="Overview"
                    >
                        <span className="nav-icon">📊</span>
                        {!navCollapsed && <span className="nav-label">Overview</span>}
                    </Link>

                    {/* Assets - Expandable Section */}
                    <div className="nav-section">
                        <button
                            className={`nav-item nav-section-header ${isActive('/assets') ? 'active' : ''}`}
                            onClick={() => !navCollapsed && toggleSection('assets')}
                            title="Assets"
                        >
                            <span className="nav-icon">🌐</span>
                            {!navCollapsed && (
                                <>
                                    <span className="nav-label">Assets</span>
                                    <span className="nav-expand-icon">
                                        {sectionsExpanded.assets ? '▼' : '▶'}
                                    </span>
                                </>
                            )}
                        </button>

                        {!navCollapsed && sectionsExpanded.assets && (
                            <div className="nav-section-content">
                                {/* Manage All Link */}
                                <Link
                                    to="/assets"
                                    className={`nav-subitem ${location.pathname === '/assets' ? 'active' : ''}`}
                                >
                                    Manage All
                                </Link>

                                {/* Domains */}
                                {domains.length > 0 && (
                                    <div className="nav-asset-group">
                                        <span className="nav-group-label">Domains</span>
                                        {domains.map(asset => (
                                            <Link
                                                key={asset.assetId}
                                                to={`/assets/${asset.assetId}`}
                                                className={`nav-asset-item ${location.pathname === `/assets/${asset.assetId}` ? 'active' : ''}`}
                                            >
                                                <span className={`nav-risk-dot ${getRiskDotClass(asset)}`}></span>
                                                <span className="nav-asset-name">{asset.value}</span>
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                {/* IP Ranges */}
                                {ipAssets.length > 0 && (
                                    <div className="nav-asset-group">
                                        <span className="nav-group-label">IP Ranges</span>
                                        {ipAssets.map(asset => (
                                            <Link
                                                key={asset.assetId}
                                                to={`/assets/${asset.assetId}`}
                                                className={`nav-asset-item ${location.pathname === `/assets/${asset.assetId}` ? 'active' : ''}`}
                                            >
                                                <span className={`nav-risk-dot ${getRiskDotClass(asset)}`}></span>
                                                <span className="nav-asset-name">{asset.value}</span>
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                {/* Empty state */}
                                {!assetsLoading && assets.length === 0 && (
                                    <span className="nav-empty">No assets monitored</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Drifts - Expandable Section */}
                    <div className="nav-section">
                        <button
                            className={`nav-item nav-section-header ${isActive('/drift-alarms') || isActive('/drift-analytics') || isActive('/drift-rules') ? 'active' : ''}`}
                            onClick={() => !navCollapsed && toggleSection('drifts')}
                            title="Drifts"
                        >
                            <span className="nav-icon">⚡</span>
                            {!navCollapsed && (
                                <>
                                    <span className="nav-label">Drifts</span>
                                    <span className="nav-expand-icon">
                                        {sectionsExpanded.drifts ? '▼' : '▶'}
                                    </span>
                                </>
                            )}
                        </button>

                        {!navCollapsed && sectionsExpanded.drifts && (
                            <div className="nav-section-content">
                                <Link
                                    to="/drift-alarms"
                                    className={`nav-subitem ${isActive('/drift-alarms') ? 'active' : ''}`}
                                >
                                    Drift Alarms
                                </Link>
                                <Link
                                    to="/drift-analytics"
                                    className={`nav-subitem ${isActive('/drift-analytics') ? 'active' : ''}`}
                                >
                                    Drift Analytics
                                </Link>
                                <Link
                                    to="/drift-rules"
                                    className={`nav-subitem ${isActive('/drift-rules') ? 'active' : ''}`}
                                >
                                    Drift Rules
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Inventory - Single Link */}
                    <Link
                        to="/inventory"
                        className={`nav-item ${isActive('/inventory') ? 'active' : ''}`}
                        title="Inventory"
                    >
                        <span className="nav-icon">📦</span>
                        {!navCollapsed && <span className="nav-label">Inventory</span>}
                    </Link>

                    {/* Scans - Expandable Section (includes Schedules) */}
                    <div className="nav-section">
                        <button
                            className={`nav-item nav-section-header ${isActive('/fast-scan') || isActive('/comprehensive-scan') || isActive('/schedules') ? 'active' : ''}`}
                            onClick={() => !navCollapsed && toggleSection('scans')}
                            title="Scans"
                        >
                            <span className="nav-icon">🔍</span>
                            {!navCollapsed && (
                                <>
                                    <span className="nav-label">Scans</span>
                                    <span className="nav-expand-icon">
                                        {sectionsExpanded.scans ? '▼' : '▶'}
                                    </span>
                                </>
                            )}
                        </button>

                        {!navCollapsed && sectionsExpanded.scans && (
                            <div className="nav-section-content">
                                <Link
                                    to="/fast-scan"
                                    className={`nav-subitem ${isActive('/fast-scan') ? 'active' : ''}`}
                                >
                                    Fast Scan
                                </Link>
                                <Link
                                    to="/comprehensive-scan"
                                    className={`nav-subitem ${isActive('/comprehensive-scan') ? 'active' : ''}`}
                                >
                                    Deep Scan
                                </Link>
                                <Link
                                    to="/schedules"
                                    className={`nav-subitem ${isActive('/schedules') ? 'active' : ''}`}
                                >
                                    Schedules
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Reports - Single Link */}
                    <Link
                        to="/reports"
                        className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
                        title="Reports"
                    >
                        <span className="nav-icon">📄</span>
                        {!navCollapsed && <span className="nav-label">Reports</span>}
                    </Link>

                    {/* Suppressions - Single Link */}
                    <Link
                        to="/suppressions"
                        className={`nav-item ${isActive('/suppressions') ? 'active' : ''}`}
                        title="Suppressions"
                    >
                        <span className="nav-icon">🔇</span>
                        {!navCollapsed && <span className="nav-label">Suppressions</span>}
                    </Link>

                    {/* Settings - Single Link */}
                    <Link
                        to="/settings"
                        className={`nav-item ${isActive('/settings') ? 'active' : ''}`}
                        title="Settings"
                    >
                        <span className="nav-icon">⚙️</span>
                        {!navCollapsed && <span className="nav-label">Settings</span>}
                    </Link>
                </div>

                {/* Footer */}
                {!navCollapsed && (
                    <div className="nav-footer">
                        <Link to="/dashboard-prototypes" className="nav-proto-link">
                            ← Prototypes
                        </Link>
                    </div>
                )}
            </nav>

            <main className="cm-main-content">
                {children}
            </main>
        </div>
    );
}

export default CleanModernLayout;
